import React from 'react';

import { Upload, message, Typography, Collapse, Row, Tooltip, Spin } from 'antd';
import {
  DeleteOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileExcelOutlined,
  VideoCameraOutlined,
  AudioOutlined,
} from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';

import { ClientContext } from '../../contexts';

import UploadImage from '../../assets/img/image.svg';

import {
  MediaUploaderProps,
  MediaUploaderState,
  UploadedMediaType,
  UploadedMediaApiResponseType,
} from './types';

import styles from './MediaUploader.module.css';

const { Text } = Typography;

// La taille maximale acceptée est 100 MB (104857600 Bytes)
const VALID_SIZE = 104857600;

const VALID_FORMAT_LIST_IMAGE_ONLY = ['image/jpg', 'image/jpeg', 'image/png'];

const VALID_FORMAT_LIST = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip',
  'application/x-rar-compressed',
  'application/x-rar',
  'application/x-zip-compressed',
  'audio/mpeg',
  '',
];

/* Audio accepted in the publication forms (with the videos): mp3, wav, m4a, ogg */
const AUDIO_FORMAT_LIST = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/ogg',
];

const VIDEO_FORMAT_LIST = [
  'video/x-flv',
  'video/mp4',
  'application/x-mpegURL',
  'video/MP2T',
  'video/3gpp',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-ms-wmv',
];

export class MediaUploader extends React.Component<
  MediaUploaderProps,
  MediaUploaderState
> {
  /* Nombre d'uploads en cours (pour gérer l'import simultané de plusieurs fichiers) */
  private inFlight = 0;

  constructor(props: MediaUploaderProps) {
    super(props);
    this.state = {
      mediaList: props.defaultValue || [],
      clientContext: undefined,
      uploading: false,
    };

    this.beforeUpload = this.beforeUpload.bind(this);
    this.handleUpload = this.handleUpload.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.getMediaViewer = this.getMediaViewer.bind(this);
  }

  beforeUpload(file: RcFile) {
    const { imageOnly, withVideo } = this.props;

    const isFormatValid = this.getMediaUploaderAcceptList(file);

    const isSizeValid = file.size < VALID_SIZE;

    if (!isFormatValid) {
      message.error(
        !imageOnly
          ? 'Les formats acceptés sont les suivants : .jpg , .jpeg , .png , .gif , .pdf, .ppt, .pptx, .xls, .xlsx, .csv, .doc, .docx, .zip, .rar, .mp3'
          : `Les formats acceptés sont les suivants : .jpg , .jpeg , .png ${
              withVideo
                ? ', .mp4, .3gp, .mov, .avi, .wmv, .mp3, .wav, .m4a, .ogg'
                : ''
            }`
      );
    }
    if (!isSizeValid) {
      message.error(
        'Veuillez importer une photo ou un document inférieur à 100 Mo'
      );
    }
    return isFormatValid && isSizeValid;
  }

  handleUpload(file: File) {
    const { clientContext } = this.state;
    if (!clientContext) return;

    const { directory, onUpload } = this.props;
    const { get, getToken } = clientContext;

    const MEDIA_ROUTE = !directory
      ? '/medias/upload'
      : `/medias/upload?directory=${directory.id.replace(
          'directory?directoryId=',
          ''
        )}`;

    const formData = new FormData();
    formData.append('file', file);

    /* Mécanisme d'upload inchangé (fetch) ; on ajoute juste un état "uploading"
       (compteur pour gérer plusieurs fichiers) afin d'afficher un indicateur. */
    this.inFlight += 1;
    this.setState({ uploading: true });

    const finish = () => {
      this.inFlight = Math.max(0, this.inFlight - 1);
      this.setState({ uploading: this.inFlight > 0 });
    };

    fetch(`${process.env.REACT_APP_MR_CLIENT_API_URL}${MEDIA_ROUTE}`, {
      body: formData,
      headers: {
        Authorization: `Bearer ${getToken().accessToken}`,
      },
      method: 'post',
    })
      .then(response => {
        finish();
        get(
          `/medias/${response.headers.get('Location')?.split('/')?.pop()!}`,
          (media: UploadedMediaApiResponseType) => {
            media.data &&
              this.setState(
                {
                  mediaList: [
                    {
                      id: media.data.id,
                      url: media.data.url.original,
                      name: media.data.name,
                      type: media.data.mime,
                    },
                    ...this.state.mediaList,
                  ],
                },
                () => onUpload(this.state.mediaList)
              );
          }
        );
      })
      .catch(() => {
        finish();
        message.error('Une erreur est survenue lors du de téléchargement');
      });
  }

  handleRemove(mediaId: string) {
    const { clientContext } = this.state;
    const { onUpload } = this.props;

    if (clientContext) {
      const { deletion } = clientContext;
      deletion(
        `/medias/${mediaId}`,
        {},
        () => {
          const filteredMedia = this.state.mediaList.filter(media => {
            if (media.uid) {
              return media.uid !== mediaId;
            } else {
              return media.id !== mediaId;
            }
          });

          this.setState(
            {
              mediaList: filteredMedia,
            },
            () => onUpload(filteredMedia)
          );

          this.props.isRemoved && this.props.isRemoved(mediaId);
        },
        () => {
          message.error(
            'Une erreur est survenue lors de suppression de la photo ou du document'
          );
        }
      );
    }
  }

  getMediaUploaderAcceptList(file: RcFile) {
    const { validFormatList, imageOnly, withVideo } = this.props;

    if (validFormatList) return validFormatList.includes(file.type);

    if (imageOnly) return VALID_FORMAT_LIST_IMAGE_ONLY.includes(file.type);

    if (withVideo)
      return [
        ...VALID_FORMAT_LIST,
        ...VIDEO_FORMAT_LIST,
        ...AUDIO_FORMAT_LIST,
      ].includes(file.type);

    return VALID_FORMAT_LIST.includes(file.type);
  }

  getMediaIcon(media: UploadedMediaType) {
    if (AUDIO_FORMAT_LIST.includes(media.type))
      return (
        <>
          <AudioOutlined className={styles.document} />
          <Tooltip placement="top" title={media.name}>
            <p className={styles.mediaName}>{media.name}</p>
          </Tooltip>
        </>
      );

    if (VIDEO_FORMAT_LIST.includes(media.type))
      return (
        <>
          <VideoCameraOutlined className={styles.document} />
          <Tooltip placement="top" title={media.name}>
            <p className={styles.mediaName}>{media.name}</p>
          </Tooltip>
        </>
      );

    const extensionFileTab = media.name?.split('.');
    const extensionFile =
      extensionFileTab && extensionFileTab[extensionFileTab.length - 1];

    switch (extensionFile) {
      case 'doc':
      case 'docx':
        return (
          <>
            <FileTextOutlined className={styles.document} />
            <p className={styles.mediaName}>{media.name}</p>
          </>
        );
      case 'gif':
      case 'jpeg':
      case 'jpg':
      case 'png':
        return (
          <img src={media.url} alt={media.name} className={styles.image} />
        );
      case 'ppt':
      case 'pptx':
        return (
          <>
            <FilePptOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      case 'zip':
      case 'rar':
        return (
          <>
            <FileZipOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      case 'xls':
      case 'xlsx':
      case 'csv':
        return (
          <>
            <FileExcelOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      case 'pdf':
        return (
          <>
            <FilePdfOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      default:
        return (
          <img src={media.url} alt={media.name} className={styles.image} />
        );
    }
  }

  getMediaViewer(media: UploadedMediaType) {
    return (
      <div
        key={media.url}
        className={styles.mediaContainer}
        data-testid="media-uploader-view"
        onClick={
          this.props.viewDocument ? () => window.open(media.url) : () => {}
        }
      >
        {this.getMediaIcon(media)}
        {!this.props.isReporter && (
          <DeleteOutlined
            id="remove-icon"
            className={styles.removeIcon}
            onClick={e => {
              e.stopPropagation();
              this.handleRemove(media.uid || media.id);
            }}
          />
        )}
      </div>
    );
  }

  renderUploader() {
    const { placeholder, children, limit, defaultValue, pictureListType } =
      this.props;
    const { mediaList, uploading } = this.state;
    const { Panel } = Collapse;

    const excludeCollapse = ['Couverture', 'Avatar', 'Télécharger des photos'];
    return (
      <div>
        {uploading && (
          <div
            data-testid="media-uploader-progress"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
            }}
          >
            <Spin size="small" />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Import en cours…
            </Text>
          </div>
        )}
        {placeholder &&
        !excludeCollapse.includes(placeholder) &&
        pictureListType === 'picture-card' ? (
          <>
            <Collapse defaultActiveKey={['1']}>
              <Panel
                id="target"
                showArrow={false}
                header={
                  <div className={styles.panelTitle} id="target-header">
                    <img src={UploadImage} alt="UploadImage" /> {placeholder}
                  </div>
                }
                key="2"
              >
                <Row>
                  <div className={!limit ? styles.uploadeContainer : undefined}>
                    <div className={styles.docList}>
                      {mediaList.map((media: UploadedMediaType) =>
                        this.getMediaViewer(media)
                      )}
                    </div>
                    {(!limit || (limit && mediaList.length < limit)) && (
                      <Upload
                        id={this.props.id || 'media-uploader'}
                        action={
                          'https://www.mocky.io/v2/5cc8019d300000980a055e76'
                        }
                        data-testid="media-uploader"
                        className={styles.uploadButton}
                        listType={pictureListType}
                        fileList={this.props.canRemove ? [] : defaultValue}
                        beforeUpload={this.beforeUpload}
                        showUploadList={false}
                        // @ts-ignore
                        data={this.handleUpload}
                        multiple={true}
                        disabled={this.props.disabled}
                      >
                        {children}
                      </Upload>
                    )}
                  </div>
                </Row>
              </Panel>
            </Collapse>
          </>
        ) : (
          <>
            {placeholder &&
              excludeCollapse.includes(placeholder) &&
              pictureListType === 'picture-card' && (
                <Text className={styles.placeholder} strong={true}>
                  {placeholder}
                </Text>
              )}
            <div className={!limit ? styles.uploadeContainer : undefined}>
              <div className={styles.docList}>
                {mediaList.map((media: UploadedMediaType) =>
                  this.getMediaViewer(media)
                )}
              </div>
              {(!limit || (limit && mediaList.length < limit)) && (
                <Upload
                  id={this.props.id || 'media-uploader'}
                  action={'https://www.mocky.io/v2/5cc8019d300000980a055e76'}
                  data-testid="media-uploader"
                  className={styles.uploadButton}
                  listType={pictureListType}
                  fileList={this.props.canRemove ? [] : defaultValue}
                  beforeUpload={this.beforeUpload}
                  showUploadList={false}
                  // @ts-ignore
                  data={this.handleUpload}
                  multiple={true}
                  disabled={this.props.disabled}
                >
                  {children}
                </Upload>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  render() {
    const { clientContext } = this.state;
    return (
      <ClientContext.Consumer>
        {context => {
          !clientContext && this.setState({ clientContext: context });
          return this.renderUploader();
        }}
      </ClientContext.Consumer>
    );
  }
}
